import {
	devLog,
	getFeaturedImage,
	getCustomData,
	isDev,
	promiser,
	isEmpty,
	identify,
	getUser,
} from 'core/utils';
import humps from 'humps';
import flatten from 'core/utils/flatten';
import ApiRequest from 'core/api/request';
import appboy from 'appboy';

const TEST_ENDPOINT = `8790b478-b534-4bfc-b6d2-e69d05088574`;
const PROD_ENDPOINT = `8af24d00-2561-4793-be56-606c49c19f7a`;
const baseUrl = `https://sdk.iad-03.braze.com/api/v3`;

export const initialize = async () => {
	if (! window) return;
	const env = process.env.NODE_ENV;
	const shouldUseDevEnv = env === 'development' || window.cj.environment === 'stage';
	const endpoint = shouldUseDevEnv ? TEST_ENDPOINT : PROD_ENDPOINT;

	appboy.initialize(endpoint, {
		baseUrl,
		enableLogging: isDev(),
	});

	await identify({ force: false });

	appboy.openSession();
};

// Deleting arrays because Braze doesn't like them
const cleanData = data => {
	const keys = Object.keys(data);
	keys.forEach(k => {
		const test = data[k];
		if (Array.isArray(test)) return delete data[k];
		if (test === "") return delete data[k];
		if (test === undefined) return delete data[k];
		if (test === null) return delete data[k];
	});
	return data;
};

export const track = async (name, data = {}) => {
	if (! name) return;
	data.event = name;
	const decamelizedData = humps.decamelizeKeys(data);
	const flattenedData = flatten(decamelizedData, { delimiter: '_' });
	const cleanedData = cleanData(flattenedData);
	devLog({ eventName: `${name} Braze`, cleanedData });
	appboy.logCustomEvent(name, cleanedData);
};

// Like 'track' but go via our backend to dedupe first.
export const track_guest = async (name, data = {}) => {
	if (! name) return;
	data.event = name;
	const decamelizedData = humps.decamelizeKeys(data);
	const flattenedData = flatten(decamelizedData, { delimiter: '_' });
	const cleanedData = cleanData(flattenedData);

	// Send to our backend
	const [error, res] = await promiser(ApiRequest.post("/t/g/", cleanedData));
};

const sendPurchaseAttributes = (order, cart) => {
	// Update user attributes
	const {
		categoriesAddedToCart,
		subcategoriesAddedToCart,
		collectionsAddedToCart,
		slugsAddedToCart,
	} = cart.products.reduce((res, p) => {
		// Combine all cartProduct attributes into one object
		if (!p.listing) {
			return res;
		}
		const attributes = getCartAttributes(p.listing);
		Object.keys(attributes).forEach(key => {
			const value = attributes[key];
			res[key] = res[key] || [];
			res[key] = [...res[key], ...value];
		});
		return res;
	}, {});

	const attributes = order.isGift ? {
		categoriesGifted: categoriesAddedToCart,
		subcategoriesGifted: subcategoriesAddedToCart,
		collectionsGifted: collectionsAddedToCart,
		slugsGifted: slugsAddedToCart,
		hasPurchasedGift: true,
		lastGiftDate: new Date(),
	} : {
		categoriesPurchased: categoriesAddedToCart,
		subcategoriesPurchased: subcategoriesAddedToCart,
		collectionsPurchased: collectionsAddedToCart,
		slugsPurchased: slugsAddedToCart,
		lastPurchaseDate: new Date(),
	};

	return updateUserAttributes(attributes);
};

/**
 * Record transaction conversion with Braze
 *
 * @param {Object} order
 * @param {Object} cart
 */
export const completeTransaction = async (order, cart) => {
	if (! order || ! cart) return;
	const cartProducts = cart.products.filter(p => !!p.price) || [];
	if (cartProducts.length === 0) return;

	sendPurchaseAttributes(order, cart);

	const products = cartProducts.reduce((res, p) => {
		// Favor Product Listing ID
		const brazeProductId = p?.listing?.listingId ? p.listing.listingId : (
			// Otherwise for sub products, use [store] listing id
			// Final fallback product id (OTP without PDP)
			p.subscriptionTypeTermId ? cart?.listing?.listingId : p?.productInstance?.product?.id
		);
		res[brazeProductId] = {
			price: (p.price || 0) / 100,
			quantity: p.quantity || 1,
			instanceId: p.productInstanceId,
			productId: p.productInstance?.product?.id,
			storeId: order.storeId,
			listingId: p?.listing?.listingId ? p.listing.listingId : cart?.listing?.listingId
		};

		return res;
	}, {});

	const data = {
		products,
		currencyCode: order?.currencyCode || cart?.store?.currencyCode || 'USD',
		// Additional properties sent to purchase event
		// NOTE: We rarely (if ever) use; could segment or reference with Liquid templates (via `event_properties`)
		// Properties stored here are only valid for 30 days and do not perist to user attributes
		// https://www.braze.com/docs/api/objects_filters/purchase_object/#purchase-properties-object
		// https://www.braze.com/docs/user_guide/data_and_analytics/custom_data/purchase_events/#purchase-properties
		properties: {
			autorenew: order.subscriptions?.some(s => s.autorenew),
			isGift: order.isGift,
			totalPrice: order.totalWithServiceFee / 100,
			totalShipping: order.totalShipping / 100,
			category: order.featuredCategory,
			orderPlacedAt: order.placedAt,
			couponCode: order.couponCode, // TODO: support multiple coupons
		},
	};
	devLog(`Sending purchase to Braze`, { purchaseData: data });
	let res;
	// Dont use promiser here. It offuscates the error stacktrace
	try {
		res = await ApiRequest.post("/t/f/", data);
	} catch (e) {
		devLog(e);
	}
	if (res?.message === "success") {
		devLog(`BRAZE: Success tracking purchase`);
	} else {
		devLog({ brazePurchaseRes: res });
	}
};


export const getProductUrl = listing => {
	if (listing.listing) {
		if (listing.listing.slug) {
			return 'https://www.cratejoy.com/subscription-box/' + listing.listing.slug + '/';
		}
	}

	if (listing.slug) {
		return 'https://www.cratejoy.com/subscription-box/' + listing.slug + '/';
	}
};

export const getListingProps = (listing, blob = {}) => {
	// TODO: check usage in Braze, should be listing.product_id
	blob.productId = listing.listingId || listing.listing_id;
	var listingImages = listing.listingImages || listing.listing_images;
	if (listingImages && listingImages.length) {
		const img = listingImages[0];
		blob.productImageUrl = img.cdnUrl;
	}

	blob.productUrl = getProductUrl(listing);

	return blob;
};

export const getBrazeCartProps = (listing, product) => {
	const category = listing.categories[0] || {};
	const listingFeaturedImage = getFeaturedImage({ listingImages: listing.listingImages || listing.listing_images });
	const imageUrl = listingFeaturedImage.cdnUrl || "";

	let props = {
		listingId: listing.listingId,
		listingVersionId: listing.listingVersionId,
		quantity: 1,
		category: category.categoryName,
		slug: listing.slug,
		listingName: listing.name,
		currencyCode: listing.currencyCode,
		currencySymbol: listing.currencySymbol,
		listingBriefDescription: listing.briefDescription,
		listingMinPrice: listing.minUnitTermPrice / 100,
		listingFeaturedImage: imageUrl,
		sentAt: new Date(),
		urlParams: window.location.search,
		basicInfo: true,
	};

	if (product) {
		const productProps = {
			productName: product.productInstance.product.name,
			variant: product.productInstanceId + '_' + product.subscriptionTypeTermId,
			productInstanceId: product.productInstanceId,
			subscriptionTypeTermId: product.subscriptionTypeTermId,
			price: product.price / 100,
		};

		props = {
			...props,
			...productProps,
		};

		// Let Braze know we have product info for the mails
		props.basicInfo = false;
	}

	return props;
};

export const getItemProps = cartItem => {
	const listing = getCustomData(window.cj.listing, {});

	// TODO: Listing is undef in multicart.
	// We need to get the same data attached to window.cj.app_data
	// as the regular cart.
	if (!listing.id) return {};

	const category = listing.categories[0] || {};
	const listingFeaturedImage = getFeaturedImage(listing);
	const imageUrl = listingFeaturedImage.cdnUrl || "";

	let props = {
		listingId: listing.listingId,
		productId: listing.productId,
		listingVersionId: listing.listingVersionId,
		quantity: 1,
		category: category.categoryName,
		slug: listing.slug,
		listingName: listing.name,
		currencyCode: listing.currencyCode,
		currencySymbol: listing.currencySymbol,
		listingBriefDescription: listing.briefDescription,
		listingMinPrice: listing.minUnitTermPrice / 100,
		listingFeaturedImage: imageUrl,
		sentAt: new Date(),
		urlParams: window.location.search,
		basicInfo: true,
	};

	props.productUrl = getProductUrl(listing);

	// Enhance data if we have an item/product available
	// We only have this once a term is selected
	if (cartItem) {
		const product = cartItem.get('products').at(0);
		const productProps = {
			productName: product.get('productInstance').get('product').get('name'),
			variant: product.get('productInstanceId') + '_' + product.get('subscriptionTypeTermId'),
			productInstanceId: product.get('productInstanceId'),
			subscriptionTypeTermId: product.get('subscriptionTypeTermId'),
			price: product.get('price') / 100,
		};

		props = {
			...props,
			...productProps,
		};

		// Let Braze know we have product info for the mails
		props.basicInfo = false;
	}

	return props;
};

const getCartAttributes = (listing=undefined) => {
	const listingForAttributes = listing ? listing : getCustomData(window.cj.listing, {});
	const categories = listingForAttributes.categories || [];
	const categoriesAddedToCart = [];
	const subcategoriesAddedToCart = [];
	categories.forEach(category => {
		const parentCatName = category?.parent?.categoryName;
		return parentCatName
			// if it has a parent then its a subcategory
			? subcategoriesAddedToCart.push(category.slug)
			: categoriesAddedToCart.push(category.slug);
	});

	const collections = listingForAttributes.collections || [];
	const collectionsAddedToCart = collections.map(({ slug }) => slug);
	const attributes = {
		categoriesAddedToCart,
		subcategoriesAddedToCart,
		collectionsAddedToCart,
		slugsAddedToCart: [listingForAttributes.slug],
	};

	return attributes;
};

export const addToCart = async cartItem => {
	const data = getItemProps(cartItem);
	const decamelizedData = humps.decamelizeKeys(data);
	// Send braze a different event depending on if we have a cart item already...
	if (cartItem) {
		devLog(`Braze add_to_cart event:`, decamelizedData);
		track("add_to_cart_lite_exception");
		track("add_to_cart", decamelizedData);
	} else {
		devLog(`Braze add_to_cart_lite event:`, decamelizedData);
		track("add_to_cart_lite", decamelizedData);
	}
	updateUserAttributes(getCartAttributes());
};

export const addToCartPPL = async props => {
	const decamelizedData = humps.decamelizeKeys(props);
	devLog(`Braze add_to_cart_ppl event:`, decamelizedData);
	await track("add_to_cart", decamelizedData);
	// TODO: Create something like getCartAttributes
	// updateUserAttributes(getCartAttributes());
};

/**
 * At login or guest checkout, send an event to Braze as soon as
 * we have an email address so that we can detect abandoned carts later on.
 */
export const addToCartGuest = async (email, cartId) => {
	const decamelizedData = humps.decamelizeKeys({ ...getItemProps(undefined), email, cartId });

	devLog(`Braze add_to_cart_guest event:`, decamelizedData);
	track_guest("add_to_cart_guest", decamelizedData);
};

/**
 * updateUserAttributes
 * @see https://www.braze.com/docs/developer_guide/platform_integration_guides/web/analytics/setting_custom_attributes/
 * @param {Object} attributes - Attributes to update
 */
export const updateUserAttributes = attributes => {
	if (! getUser()) return;
	if (isEmpty(attributes)) return;

	// Prepare attributes
	const keys = Object.keys(attributes);
	keys.forEach(key => {
		const checkValue = attributes[key];
		// If you're an empty array i don't want it
		if (Array.isArray(checkValue)) {
			if (checkValue.length === 0) return delete attributes[key];
		}
	});

	const data = humps.decamelizeKeys(attributes);
	devLog(`Braze updating user attributes:`, data);
	const humpedKeys = Object.keys(data);
	humpedKeys.forEach(key => {
		const checkValue = data[key];
		// If you're an empty array i don't want it
		if (Array.isArray(checkValue)) {
			return checkValue.forEach(val => {
				appboy.getUser().addToCustomAttributeArray(key, val);
			});
		}
		return appboy.getUser().setCustomUserAttribute(key, checkValue);
	});
};

const BrazeUtil = {
	initialize,
	getListingProps,
	getItemProps,
	track,
	completeTransaction,
	addToCart,
	addToCartGuest,
	updateUserAttributes,
	addToCartPPL,
};

export default BrazeUtil;
